import React, { useState, useEffect } from 'react';
import { userService } from "../../../_services";
import { Navigate } from 'react-router-dom';
import moment from 'moment';
import UserDashboard from "../user/Dashboard";
import DateRangePicker from "@/components/ui/date-range-picker";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ExternalLink, Search, Loader2 } from "lucide-react";

/**
 * User Transactions Page
 * Modernized with shadcn components - Claims dashboard for distributors
 */
function Transactions() {
    // Initialize dates
    const getInitialStartDate = () => {
        const stored = localStorage.getItem('User_Start_Date');
        if (stored && stored !== "" && stored !== null && stored !== undefined) {
            return stored;
        }
        const date = new Date();
        date.setDate(date.getDate() - 30);
        return date.toISOString().split('T')[0];
    };

    const getInitialEndDate = () => {
        const stored = localStorage.getItem('User_End_Date');
        if (stored && stored !== "" && stored !== null && stored !== undefined) {
            return stored;
        }
        return new Date().toISOString().split('T')[0];
    };

    // State
    const [dashboardListRendered, setDashboardListRendered] = useState(true);
    const [startDate, setStartDate] = useState(getInitialStartDate());
    const [endDate, setEndDate] = useState(getInitialEndDate());
    const [allDirectOverride, setAllDirectOverride] = useState('Direct');
    const [userId, setUserId] = useState('');
    const [userName, setUserName] = useState('');
    const [dashboardName, setDashboardName] = useState('');
    const [roleType, setRoleType] = useState('Wholesaler');
    const [roleName, setRoleName] = useState('patient rx discount');
    const [searchInput, setSearchInput] = useState('');
    const [searchDisplay, setSearchDisplay] = useState([]);
    const [showSearchResults, setShowSearchResults] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [selectedTab, setSelectedTab] = useState('Total');
    const [isDistributor, setIsDistributor] = useState(false);
    const [overrideCount, setOverrideCount] = useState(0);
    const [filterKey, setFilterKey] = useState(0);

    useEffect(() => {
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [id, name] = Usermeta_Data.split(':');
        setUserId(id);
        setUserName(name);
        setDashboardName(name);

        // Initialize claim type
        window.localStorage.setItem('Updated_HD_Type', 'totalpaid');

        // Check if distributor and set role info for search
        const userRole = window.atob(localStorage.getItem('_Host_Pro_type'));
        if (userRole === 'Distributor') {
            setIsDistributor(true);
            setRoleType(userRole);
            setRoleName(name);
            userService.GetUserOverride('', id)
                .then(apidata => {
                    if (apidata && apidata.length > 0) {
                        setOverrideCount(apidata[0].overrideCount || 0);
                    }
                })
                .catch(err => console.error('Error fetching override count:', err));
        }
    }, []);

    const handleChangeStartEnd = (type, value) => {
        if (type === 'start') {
            setStartDate(value);
            localStorage.setItem('User_Start_Date', value);
        } else if (type === 'end') {
            setEndDate(value);
            localStorage.setItem('User_End_Date', value);
        }
    };

    const handleDateRangeChange = (event, picker) => {
        const newStartDate = picker.startDate.format('YYYY-MM-DD');
        const newEndDate = picker.endDate.format('YYYY-MM-DD');
        setStartDate(newStartDate);
        setEndDate(newEndDate);
        localStorage.setItem('User_Start_Date', newStartDate);
        localStorage.setItem('User_End_Date', newEndDate);
    };

    const handleChangeAllDirectOverride = (value) => {
        setAllDirectOverride(value);
        // Increment filterKey so user/Dashboard re-fetches with the new API base URL.
        // The old class component forced this via setState({ userdata, AllDirectOverride })
        // which triggered componentDidUpdate → changeData(). filterKey is the modern equivalent.
        setFilterKey(prev => prev + 1);
    };

    // Mirrors Create_Advance_FIlter_Json_Without_Refresh('PBM','HD','RadioSelect') from reference branch.
    const applyPCNHDFilter = () => {
        try {
            let filter = null;
            try { filter = JSON.parse(localStorage.getItem('Advance_Filter')); } catch (_) {}
            if (filter && typeof filter === 'object') {
                filter['PBM'] = ['HD'];
                localStorage.setItem('Advance_Filter', JSON.stringify(filter));
            } else {
                localStorage.setItem('Advance_Filter', JSON.stringify({ PBM: ['HD'] }));
            }
            const now = new Date().toString();
            localStorage.setItem('Filter_Last_Update_Time', now);
            localStorage.setItem('Updated_AdFilter_Status', localStorage.getItem('Advance_Filter'));
        } catch (_) {}
    };

    // Remove PCN:HD filter when returning to Total tab
    const removePCNHDFilter = () => {
        try {
            let filter = null;
            try { filter = JSON.parse(localStorage.getItem('Advance_Filter')); } catch (_) {}
            if (filter && typeof filter === 'object' && filter['PBM']) {
                delete filter['PBM'];
                const updated = Object.keys(filter).length === 0 ? null : JSON.stringify(filter);
                localStorage.setItem('Advance_Filter', updated);
                const now = new Date().toString();
                localStorage.setItem('Filter_Last_Update_Time', now);
                localStorage.setItem('Updated_AdFilter_Status', updated || '');
            }
        } catch (_) {}
    };

    const handleTabChange = (value) => {
        setSelectedTab(value);
        let claimType = 'totalpaid';
        if (value === 'Reversed') claimType = 'reversed';
        else if (value === 'Net') claimType = 'netpaid';
        else if (value === 'Paid') claimType = 'compensable';

        // Save current as Prev before overwriting (mirrors reference branch logic)
        localStorage.setItem('Prev_HD_Type', localStorage.getItem('Updated_HD_Type'));
        window.localStorage.setItem('Updated_HD_Type', claimType);

        if (claimType !== 'totalpaid') {
            applyPCNHDFilter();
        }
        // PCN:HD filter is only removed when the user explicitly presses X or Reset

        setFilterKey(prev => prev + 1);
    };

    const handleChangeFilter = (e) => {
        // Sync tab back to Total when Updated_HD_Type was reset to totalpaid by FilterTag
        // (e.g. after Reset button or after removing the PCN:HD tag via X).
        // Use setSelectedTab directly instead of DOM click to avoid double refresh.
        const hdType = localStorage.getItem('Updated_HD_Type');
        if (!hdType || hdType === 'totalpaid') {
            setSelectedTab('Total');
        }

        // Single refresh trigger
        setFilterKey(prev => prev + 1);
    };

    const handleSearchKeyUp = async (e) => {
        const value = e.target.value.trim();
        setSearchInput(e.target.value);
        if (value.length < 2) {
            setSearchDisplay([]);
            setShowSearchResults(false);
            return;
        }
        try {
            setSearchLoading(true);
            const response = await fetch('/api/Filter/SearchOnTypeText', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text: value, roletype: roleType, rolename: roleName, whname: '61' })
            });
            const data = await response.json();
            if (data && data.length > 0) {
                setSearchDisplay(data);
                setShowSearchResults(true);
            } else {
                setSearchDisplay([]);
                setShowSearchResults(false);
            }
        } catch (err) {
            setSearchDisplay([]);
            setShowSearchResults(false);
        } finally {
            setSearchLoading(false);
        }
    };

    const handleSearchItemClick = (item) => {
        let existingFilter = {};
        try { existingFilter = JSON.parse(localStorage.getItem('Advance_Filter')) || {}; } catch (_) {}
        const key = item.ctype || item.cname;
        if (!existingFilter[key]) existingFilter[key] = [];
        if (!existingFilter[key].some(v => v.toLowerCase() === item.cvalue.toLowerCase())) {
            existingFilter[key].push(item.cvalue);
        }
        localStorage.setItem('Advance_Filter', JSON.stringify(existingFilter));
        localStorage.setItem('Filter_Last_Update_Time', new Date().toString());
        localStorage.setItem('Updated_AdFilter_Status', JSON.stringify(existingFilter));
        setSearchInput('');
        setSearchDisplay([]);
        setShowSearchResults(false);
        setFilterKey(prev => prev + 1);
    };

    const viewOldDashboard = async () => {
        try {
            const apidata = await userService.claimdashboard(userId, userName);
            if (apidata && apidata.length > 0 && apidata[0].code !== "-1") {
                const currentUrl = window.location.href;
                const wparam = localStorage.getItem('wt19:awaydpset');

                const isDev = currentUrl.toLowerCase().includes("release-backoffice") ||
                    currentUrl.toLowerCase().includes("dev-backoffice") ||
                    currentUrl.toLowerCase().includes("localhost:") ||
                    currentUrl.toLowerCase().includes("shadcn-backoffice");

                const url = isDev
                    ? `http://dev.glichealth.com/login?q=${wparam}`
                    : `https://reporting.nulifespanrx.com/login?q=${wparam}`;

                window.open(url, '_blank');
            }
        } catch (error) {
            console.error('Error accessing old dashboard:', error);
        }
    };

    // Auth check
    if (userService.Check_Null_Empty(localStorage.getItem("ajs_user_id"))) {
        return <Navigate to="/signup" replace />;
    }

    localStorage.setItem('pagename', 'claimdashboard');

    return (
        <div className="w-full space-y-6">
            {/* Heading */}
            <h1 className="text-3xl font-bold tracking-tight text-foreground mb-2">Claims Dashboard</h1>
            <p className="text-md text-primary/70 mb-4">Miss the old reporting dashboard? You can still access it <span onClick={viewOldDashboard} className="text-accent cursor-pointer">here <ExternalLink className="mr-2 h-3 w-3 inline" /></span>.</p>

            {/* Date Pickers, Search, and Tabs Row */}
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                {/* Date Range Picker */}
                <DateRangePicker
                    startDate={moment(startDate)}
                    endDate={moment(endDate)}
                    onApply={handleDateRangeChange}
                    displayMode="unified"
                    containerClass="w-full sm:w-auto"

                />

                {/* Search, All/Direct/Override, and Tabs */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full lg:w-auto">
                    <div className="relative w-full lg:flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary/50 z-10" />
                        <Input
                            className="pl-10 pr-9"
                            placeholder="Search..."
                            autoComplete="off"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyUp={handleSearchKeyUp}
                            onFocus={() => searchDisplay.length > 0 && setShowSearchResults(true)}
                            onBlur={() => setTimeout(() => setShowSearchResults(false), 150)}
                        />
                        {searchLoading && (
                            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-primary/40 z-10" />
                        )}
                        {showSearchResults && searchDisplay.length > 0 && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                                {searchDisplay.slice(0, 5).map((item, index) => (
                                    <div
                                        key={index}
                                        className="px-3 py-2 hover:bg-muted cursor-pointer border-b border-muted last:border-b-0 flex items-center justify-between gap-2"
                                        onMouseDown={() => handleSearchItemClick(item)}
                                    >
                                        <span className="text-sm font-semibold text-primary leading-tight">
                                            {item.cvalue}
                                        </span>
                                        <span className="text-xs font-semibold text-primary leading-tight">
                                            {item.cname === 'PBM' ? 'PCN' : item.cname}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* All / Direct / Override selector — only for Distributors with override users */}
                    {isDistributor && overrideCount > 0 && (
                        <Select value={allDirectOverride} onValueChange={handleChangeAllDirectOverride}>
                            <SelectTrigger className="w-full sm:w-[130px] shrink-0">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="Direct">Direct</SelectItem>
                                <SelectItem value="Override">Override</SelectItem>
                                <SelectItem value="All">All</SelectItem>
                            </SelectContent>
                        </Select>
                    )}

                    <Tabs value={selectedTab} onValueChange={handleTabChange} className="w-full sm:w-auto">
                        <TabsList className="grid grid-cols-4 w-full bg-background">
                            <TabsTrigger id="totalpaid1" value="Total">Total</TabsTrigger>
                            <TabsTrigger id="reversed1" value="Reversed">Reversed</TabsTrigger>
                            <TabsTrigger id="netpaid1" value="Net">Net</TabsTrigger>
                            <TabsTrigger id="compensable1" value="Paid">Paid</TabsTrigger>
                        </TabsList>
                    </Tabs>
                </div>
            </div>

            {/* Dashboard Content */}
            {dashboardListRendered && (
                <UserDashboard
                    dist_usertype={allDirectOverride}
                    dashboardName={dashboardName}
                    handeChangefilter={handleChangeFilter}
                    startDate={startDate}
                    endDate={endDate}
                    filterKey={filterKey}
                />
            )}
        </div>
    );
}

export default Transactions;
